import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { useField } from 'formik';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Determine if relay2 can be paired as a secondary of relay1.
 * Same-group pairing (1+2 or 3+4) is blocked on 4-relay units because
 * those share a hardware group. Units with fewer relays (e.g. 2-series)
 * only have one group, so any combination is valid.
 */
const allowedSecondaryRelay = (relay1, relay2, relayCount) => {
  if (relay1 == relay2) return false;
  // Units with < 4 relays have a single hardware group — any pairing is fine
  if (relayCount < 4) return true;
  // 4-relay units: cross-group only (1,2 can pair with 3,4 and vice versa)
  if (relay1 == 1 || relay1 == 2) return relay2 != 1 && relay2 != 2;
  if (relay1 == 3 || relay1 == 4) return relay2 != 3 && relay2 != 4;
  return false;
};

function PairRelayField({
  relayNumber,
  secondaryId,
  secondaryNumber,
  relays,
  required = false,
  size = 'normal',
  fullWidth = false,
  hiddenLabel = false,
  helperText = null,
  defaultValue = '0',
  label,
  onPaired = null,
  variant,
  ...props
}) {
  const [field, meta] = useField(props);

  const { t } = useTranslation();

  const alreadyPaired = relays.map(r => r.secondaryId).filter(id => id != 0 && id != null);
  const fieldLabel = label ?? t('sites.pairedRelay');

  return (
    <FormControl
      required={required}
      fullWidth={fullWidth}
      hiddenLabel={hiddenLabel}
      size={size}
      variant={variant}
      error={meta.error != null}
    >
      {!hiddenLabel && (
        <InputLabel id="paired-relay-select-label" htmlFor="paired-relay-select">
          {fieldLabel}
        </InputLabel>
      )}
      <Select
        labelId={hiddenLabel ? undefined : 'paired-relay-select-label'}
        id="paired-relay-select"
        value={field.value}
        defaultValue={defaultValue}
        label={hiddenLabel ? undefined : fieldLabel}
        variant={variant}
        onChange={e => {
          if (onPaired != null && typeof onPaired === 'function') {
            onPaired(e, e.target.value);
          }
          field.onChange(e);
        }}
        onBlur={event => {
          event.persist();
          event.target = { value: field.value, name: field.name };
          field.onBlur(event);
        }}
        inputProps={{
          name: props.name,
        }}
      >
        <MenuItem value={0}>{t('general.none')}</MenuItem>
        {secondaryId != null && secondaryId != 0 && secondaryNumber ? (
          <MenuItem value={secondaryId}>{`${t('sites.relay')} ` + secondaryNumber}</MenuItem>
        ) : (
          relays
            .filter(relay => allowedSecondaryRelay(relay.relayNumber, relayNumber, relays.length))
            .filter(relay => !alreadyPaired.includes(relay.id))
            .map(opt => (
              <MenuItem key={`relay-${opt.id}`} value={opt.id}>
                {`${t('sites.relay')} ` + opt.relayNumber}
              </MenuItem>
            ))
        )}
      </Select>
      {(helperText || (meta.touched && meta.error)) && (
        <FormHelperText>{meta.touched && meta.error ? meta.error : helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

PairRelayField.propTypes = {
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  fullWidth: PropTypes.bool,
  hiddenLabel: PropTypes.bool,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
};

export default PairRelayField;
